<template>
    <div class="w-full">
        <fieldset class="border border-solid border-gray-300 mt-6 p-3 w-full">
            <legend class="text-sm">Approval Required Fields</legend>

            <!-- Add new field button -->
            <div class="mb-4">
                <Button name="Add Field" icon="far fa-plus-circle" :method="addApproval" />
            </div>

            <!-- Fields list -->
            <div class="space-y-6">
                <div v-for="(approval, index) in approvals" :key="index" class="p-4 bg-gray-50 rounded-lg relative">
                    <!-- Delete button -->
                    <button v-if="index !== 0" type="button"
                        class="absolute top-2 right-2 text-red-500 hover:text-red-700" @click="deleteApproval(index)">
                        <i class="fa-solid fa-trash"></i>
                    </button>

                    <!-- Basic field info -->
                    <div class="grid md:grid-cols-3 gap-4 mb-4">
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Field Name</label>
                            <input v-model="approval.field_name" type="text" class="form-input w-full"
                                placeholder="e.g., approval_date" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                            <input v-model="approval.display_name" type="text" class="form-input w-full"
                                placeholder="e.g., Approval Date" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
                            <select v-model="approval.type" class="form-input w-full">
                                <option :value="null">Select Field Type</option>
                                <option v-for="(label, type) in fieldTypeLabels" :key="type" :value="type">
                                    {{ label }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <!-- Type-specific inputs -->
                    <div v-if="approval.type" class="mt-4">
                        <h4 class="text-sm font-medium text-gray-700 mb-2">Field Properties</h4>
                        <FieldTypeInputs :approval="approval" :index="index" @update="updateApprovalField"
                            @addOption="() => addSelectOption(approval)"
                            @removeOption="idx => removeSelectOption(approval, idx)" />
                    </div>

                    <!-- Show extra field if select/checkbox has 'other' option -->
                    <template v-if="
                        (approval.type === FIELD_TYPES.SELECT || approval.type === FIELD_TYPES.CHECKBOX)
                        && approval.selectOptions && approval.selectOptions.some(opt => opt.key === 'other' || opt.value === 'other')
                    ">
                        <div class="p-4 bg-gray-50 rounded-lg relative mt-4">
                            <div class="grid md:grid-cols-3 gap-4 mb-4">
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">Field Name</label>
                                    <input type="text" class="form-input w-full" placeholder="e.g., others_details" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                                    <input type="text" class="form-input w-full" placeholder="e.g., Orders Details" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
                                    <select class="form-input w-full">
                                        <option>Select Field Type</option>
                                        <option value="text" Select>Text</option>
                                        <option value="date">Date</option>
                                        <option value="number">Number</option>
                                        <option value="time">Time</option>
                                        <option value="datetime-local">Date &amp; Time</option>
                                        <option value="file">File Upload</option>
                                        <option value="select">Select/Dropdown</option>
                                        <option value="checkbox">Checkbox</option>
                                        <option value="radio">Radio Button</option>
                                        <option value="textarea">Textarea</option>
                                        <option value="email">Email</option>
                                        <option value="phone">Phone Number</option>
                                        <option value="url">URL</option>
                                        <option value="color">Color Picker</option>
                                        <option value="password">Password</option>
                                        <option value="hidden">Hidden Field</option>
                                        <option value="currency">Currency</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                            </div>
                            <!-- Type-specific inputs -->
                            <div class="mt-4">
                                <h4 class="text-sm font-medium text-gray-700 mb-2">Field Properties</h4>
                                <div>
                                    <div class="grid grid-cols-2 gap-2">
                                        <input type="number" class="form-input" placeholder="Min length">
                                        <input type="number" class="form-input" placeholder="Max length">
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </fieldset>
    </div>
</template>

<script>
    import { computed, watch } from 'vue'
    import Button from '@/Shared/Button'
    import FieldTypeInputs from './FieldTypeInputs.vue'
    import { useApprovalFields, FIELD_TYPES } from '../composables/useApprovalFields'

    export default {
        name: 'FinalApproval',

        components: {
            Button,
            FieldTypeInputs
        },

        props: {
            approvals: {
                type: Array,
                default: () => []
            }
        },

        emits: ['update:approvals'],

        setup(props, { emit }) {
            const {
                approvals,
                addApproval,
                deleteApproval,
                addSelectOption,
                removeSelectOption,
                updateApprovalField,
                isValid
            } = useApprovalFields(props.approvals)

            // Watch for changes in approvals and emit them
            watch(approvals, (newValue) => {
                emit('update:approvals', newValue)
            }, { deep: true })

            const fieldTypeLabels = {
                [FIELD_TYPES.TEXT]: 'Text',
                [FIELD_TYPES.DATE]: 'Date',
                [FIELD_TYPES.NUMBER]: 'Number',
                [FIELD_TYPES.TIME]: 'Time',
                [FIELD_TYPES.DATETIME]: 'Date & Time',
                [FIELD_TYPES.FILE]: 'File Upload',
                [FIELD_TYPES.SELECT]: 'Select/Dropdown',
                [FIELD_TYPES.CHECKBOX]: 'Checkbox',
                [FIELD_TYPES.RADIO]: 'Radio Button',
                [FIELD_TYPES.TEXTAREA]: 'Textarea',
                [FIELD_TYPES.EMAIL]: 'Email',
                [FIELD_TYPES.PHONE]: 'Phone Number',
                [FIELD_TYPES.URL]: 'URL',
                [FIELD_TYPES.COLOR]: 'Color Picker',
                [FIELD_TYPES.PASSWORD]: 'Password',
                [FIELD_TYPES.HIDDEN]: 'Hidden Field',
                [FIELD_TYPES.CURRENCY]: 'Currency',
                [FIELD_TYPES.OTHER]: 'Other'
            }

            return {
                approvals,
                addApproval,
                deleteApproval,
                addSelectOption,
                removeSelectOption,
                updateApprovalField,
                isValid,
                fieldTypeLabels,
                FIELD_TYPES
            }
        }
    }
</script>

<style scoped>
    .form-input {
        border-radius: 0.375rem;
        border-color: #D1D5DB;
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    }

    .form-input:focus {
        border-color: #6366F1;
        box-shadow: 0 0 0 1px #6366F1;
        outline: none;
    }
</style>
